import { useState, type ReactNode } from 'react';
import { ArrowDown, ArrowUp, Plus, Trash2 } from 'lucide-react';
import { Modale } from '../ui/Modale';
import { BoutonPrincipal, BoutonSecondaire, MessageErreur } from '../ui/Primitives';
import { ErreurApi } from '../../lib/api';
import {
  LIBELLES_TYPE_QUESTION,
  useEnregistrerModeleTest,
  type ModeleTest,
  type QuestionTest,
  type TypeQuestion,
} from '../../lib/useTestsPositionnement';

const CLASSE_CHAMP =
  'min-h-[44px] w-full rounded-xl border border-gray-200 bg-gray-50 px-3 text-sm text-codialis-navy focus:border-codialis-sky focus:outline-none focus:ring-2 focus:ring-codialis-sky/20';

const CLASSE_ICONE =
  'inline-flex h-10 w-10 shrink-0 items-center justify-center rounded-lg text-codialis-gray transition hover:bg-gray-100 hover:text-codialis-navy disabled:opacity-30';

function questionVierge(type: TypeQuestion = 'CHOIX_UNIQUE'): QuestionTest {
  return {
    enonce: '',
    type,
    points: 1,
    choix:
      type === 'REPONSE_LIBRE'
        ? []
        : [
            { libelle: '', correct: true },
            { libelle: '', correct: false },
          ],
    attendu: null,
  };
}

function Libelle({ texte, children }: { texte: string; children: ReactNode }): JSX.Element {
  return (
    <label className="block min-w-0">
      <span className="mb-1 block text-[11px] font-bold uppercase tracking-wide text-codialis-navy">
        {texte}
      </span>
      {children}
    </label>
  );
}

/**
 * Composition d'un test de positionnement : un titre, le niveau de formation
 * qu'il vise, une consigne et la liste des questions. Modifier un test ne
 * change aucun passage déjà attribué — chacun a sa copie des questions.
 */
export function EditeurTest({
  modele,
  niveaux,
  onFermer,
}: {
  modele: ModeleTest | null;
  niveaux: string[];
  onFermer: () => void;
}): JSX.Element {
  const enregistrer = useEnregistrerModeleTest();
  const [titre, setTitre] = useState(modele?.titre ?? '');
  const [niveau, setNiveau] = useState(modele?.niveau ?? niveaux[0] ?? '');
  const [consigne, setConsigne] = useState(modele?.consigne ?? '');
  const [actif, setActif] = useState(modele?.actif ?? true);
  const [questions, setQuestions] = useState<QuestionTest[]>(
    modele?.questions ?? [questionVierge()],
  );

  const modifierQuestion = (rang: number, valeurs: Partial<QuestionTest>): void =>
    setQuestions((liste) =>
      liste.map((question, index) => (index === rang ? { ...question, ...valeurs } : question)),
    );

  const deplacer = (rang: number, sens: -1 | 1): void =>
    setQuestions((liste) => {
      const copie = [...liste];
      const [question] = copie.splice(rang, 1);
      if (question) copie.splice(rang + sens, 0, question);
      return copie;
    });

  const changerType = (rang: number, type: TypeQuestion): void => {
    const question = questions[rang];
    if (!question) return;
    // Un choix unique garde une seule bonne réponse : la première cochée.
    const premiere = Math.max(0, question.choix.findIndex((choix) => choix.correct));
    modifierQuestion(rang, {
      type,
      choix:
        type === 'REPONSE_LIBRE'
          ? []
          : question.choix.length >= 2
            ? question.choix.map((choix, index) => ({
                ...choix,
                correct: type === 'CHOIX_UNIQUE' ? index === premiere : choix.correct,
              }))
            : questionVierge(type).choix,
      attendu: type === 'REPONSE_LIBRE' ? question.attendu : null,
    });
  };

  const total = questions.reduce((somme, question) => somme + (question.points || 0), 0);

  return (
    <Modale
      titre={modele ? 'Modifier le test' : 'Nouveau test de positionnement'}
      sousTitre="Les passages déjà attribués gardent leurs questions"
      ouverte
      onFermer={onFermer}
      largeur="max-w-4xl"
    >
      <div className="space-y-5 p-4 sm:p-6">
        <div className="grid grid-cols-1 gap-3 sm:grid-cols-2">
          <Libelle texte="Titre">
            <input value={titre} onChange={(e) => setTitre(e.target.value)} maxLength={150} className={CLASSE_CHAMP} />
          </Libelle>
          <Libelle texte="Niveau de formation visé">
            <select value={niveau} onChange={(e) => setNiveau(e.target.value)} className={CLASSE_CHAMP}>
              {niveaux.map((valeur) => (
                <option key={valeur} value={valeur}>
                  {valeur}
                </option>
              ))}
            </select>
          </Libelle>
        </div>
        <Libelle texte="Consigne affichée à l’apprenant (facultative)">
          <textarea
            value={consigne}
            onChange={(e) => setConsigne(e.target.value)}
            rows={2}
            maxLength={2000}
            className={`${CLASSE_CHAMP} py-2`}
          />
        </Libelle>
        <label className="flex min-h-[44px] items-center gap-2 text-sm text-codialis-navy">
          <input type="checkbox" checked={actif} onChange={(e) => setActif(e.target.checked)} className="h-5 w-5" />
          Test proposé à l’attribution
        </label>

        <ol className="space-y-4">
          {questions.map((question, rang) => (
            <li key={rang} className="space-y-3 rounded-2xl border border-gray-200 bg-white p-4">
              <div className="flex flex-wrap items-center gap-2">
                <span className="text-sm font-bold text-codialis-navy">Question {rang + 1}</span>
                <select
                  value={question.type}
                  onChange={(e) => changerType(rang, e.target.value as TypeQuestion)}
                  aria-label={`Type de la question ${rang + 1}`}
                  className="min-h-[40px] rounded-lg border border-gray-200 bg-gray-50 px-2 text-xs text-codialis-navy"
                >
                  {Object.entries(LIBELLES_TYPE_QUESTION).map(([valeur, libelle]) => (
                    <option key={valeur} value={valeur}>
                      {libelle}
                    </option>
                  ))}
                </select>
                <label className="flex items-center gap-1 text-xs text-codialis-gray">
                  Points
                  <input
                    type="number"
                    min={1}
                    max={20}
                    value={question.points}
                    onChange={(e) => modifierQuestion(rang, { points: Number(e.target.value) })}
                    aria-label={`Points de la question ${rang + 1}`}
                    className="min-h-[40px] w-16 rounded-lg border border-gray-200 bg-gray-50 px-2 text-sm text-codialis-navy"
                  />
                </label>
                <div className="ml-auto flex">
                  <button type="button" onClick={() => deplacer(rang, -1)} disabled={rang === 0} className={CLASSE_ICONE} aria-label={`Monter la question ${rang + 1}`}>
                    <ArrowUp className="h-4 w-4" aria-hidden />
                  </button>
                  <button type="button" onClick={() => deplacer(rang, 1)} disabled={rang === questions.length - 1} className={CLASSE_ICONE} aria-label={`Descendre la question ${rang + 1}`}>
                    <ArrowDown className="h-4 w-4" aria-hidden />
                  </button>
                  <button
                    type="button"
                    onClick={() => setQuestions((liste) => liste.filter((_, index) => index !== rang))}
                    disabled={questions.length === 1}
                    className={CLASSE_ICONE}
                    aria-label={`Supprimer la question ${rang + 1}`}
                  >
                    <Trash2 className="h-4 w-4" aria-hidden />
                  </button>
                </div>
              </div>

              <textarea
                value={question.enonce}
                onChange={(e) => modifierQuestion(rang, { enonce: e.target.value })}
                placeholder="Énoncé de la question"
                rows={2}
                maxLength={2000}
                aria-label={`Énoncé de la question ${rang + 1}`}
                className={`${CLASSE_CHAMP} py-2`}
              />

              {question.type === 'REPONSE_LIBRE' ? (
                <textarea
                  value={question.attendu ?? ''}
                  onChange={(e) => modifierQuestion(rang, { attendu: e.target.value || null })}
                  placeholder="Éléments attendus : ce qui guide la correction (non montré à l’apprenant)"
                  rows={2}
                  maxLength={2000}
                  aria-label={`Éléments attendus de la question ${rang + 1}`}
                  className={`${CLASSE_CHAMP} py-2`}
                />
              ) : (
                <div className="space-y-2">
                  {question.choix.map((choix, index) => (
                    <div key={index} className="flex min-w-0 items-center gap-2">
                      <input
                        type={question.type === 'CHOIX_UNIQUE' ? 'radio' : 'checkbox'}
                        name={`bonne-${rang}`}
                        checked={choix.correct}
                        onChange={() =>
                          modifierQuestion(rang, {
                            choix: question.choix.map((autre, position) => ({
                              ...autre,
                              correct:
                                question.type === 'CHOIX_UNIQUE'
                                  ? position === index
                                  : position === index
                                    ? !autre.correct
                                    : autre.correct,
                            })),
                          })
                        }
                        aria-label={`Bonne réponse : choix ${index + 1}`}
                        className="h-5 w-5 shrink-0"
                      />
                      <input
                        aria-label={`Libellé du choix ${index + 1}`}
                        value={choix.libelle}
                        onChange={(e) =>
                          modifierQuestion(rang, {
                            choix: question.choix.map((autre, position) =>
                              position === index ? { ...autre, libelle: e.target.value } : autre,
                            ),
                          })
                        }
                        placeholder={`Choix ${index + 1}`}
                        maxLength={500}
                        className={`${CLASSE_CHAMP} min-w-0 flex-1`}
                      />
                      <button
                        type="button"
                        onClick={() =>
                          modifierQuestion(rang, {
                            choix: question.choix.filter((_, position) => position !== index),
                          })
                        }
                        disabled={question.choix.length <= 2}
                        className={CLASSE_ICONE}
                        aria-label={`Retirer le choix ${index + 1}`}
                      >
                        <Trash2 className="h-4 w-4" aria-hidden />
                      </button>
                    </div>
                  ))}
                  {question.choix.length < 10 && (
                    <button
                      type="button"
                      onClick={() =>
                        modifierQuestion(rang, {
                          choix: [...question.choix, { libelle: '', correct: false }],
                        })
                      }
                      className="inline-flex min-h-[40px] items-center gap-1.5 text-xs font-semibold text-codialis-sky hover:underline"
                    >
                      <Plus className="h-3.5 w-3.5" aria-hidden />
                      Ajouter un choix
                    </button>
                  )}
                  <p className="text-[11px] text-codialis-gray">
                    Cochez la ou les bonnes réponses : la question est notée d’office, tout ou rien.
                  </p>
                </div>
              )}
            </li>
          ))}
        </ol>

        <div className="flex flex-wrap items-center justify-between gap-3">
          <BoutonSecondaire onClick={() => setQuestions((liste) => [...liste, questionVierge()])}>
            <Plus className="mr-1.5 h-4 w-4" aria-hidden />
            Ajouter une question
          </BoutonSecondaire>
          <span className="text-xs text-codialis-gray">
            {questions.length} question(s) · {total} point(s) au total
          </span>
        </div>

        {enregistrer.error instanceof ErreurApi && <MessageErreur message={enregistrer.error.message} />}

        <div className="flex flex-wrap gap-2 border-t border-gray-100 pt-4">
          <BoutonPrincipal
            onClick={() =>
              enregistrer.mutate(
                {
                  ...(modele ? { id: modele.id } : {}),
                  titre,
                  niveau,
                  consigne,
                  actif,
                  questions,
                },
                { onSuccess: onFermer },
              )
            }
            disabled={enregistrer.isPending || !titre.trim() || !niveau}
          >
            {enregistrer.isPending ? 'Enregistrement…' : 'Enregistrer le test'}
          </BoutonPrincipal>
          <BoutonSecondaire onClick={onFermer} disabled={enregistrer.isPending}>
            Annuler
          </BoutonSecondaire>
        </div>
      </div>
    </Modale>
  );
}
